import {
  useState, useEffect, useRef, type ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import InputTextLine from '../../../juristec-ui/core/InputTextLine';
import Button from '../../../juristec-ui/core/Button';
import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import Select from '../../../juristec-ui/core/Select';
import TextArea from '../../../juristec-ui/core/TextArea';
import { verifyInput } from '../../../juristec-ui/utils/validators/inputTextValidators';

import {
  ExternalContainer,
  NewTutorialContainer,
  FormLine,
  InputWrapper,
  ModalContainer,
  Preview,
  PreviewMessage,
  PreviewVideo,
  TutorialInfo,
  TutorialFields,
  Description,
  LinkSection,
  SelectContainer,
} from './styled/AddTutorial.styled';

import registerNamespace from './locales';

const namespace = registerNamespace('src/components/Modals/AddTutorial.tsx');

interface Tutorial {
  title: string;
  description: string;
  group: string;
  url: string;
}

interface Group {
  label: string;
  value: string;
  id: string;
}

interface AddTutorialProps {
  tutorialToEdit: Tutorial;
  groups: Group[];
  isEdit: boolean;
  close: () => void;
  editTutorial: (tutorial: Tutorial) => void;
  createNewTutorial: (tutorial: Tutorial) => void;
}

const newTutorialDefaultPropGroups: Group[] = [];

/**
* A component to inform the attributes of a new Tutorial
*/
const AddTutorial = ({
  close,
  tutorialToEdit = {
    title: '', description: '', group: '', url: '',
  },
  editTutorial,
  createNewTutorial,
  groups = newTutorialDefaultPropGroups,
  isEdit = false,
}: AddTutorialProps) => {
  const [tutorialGroup, setTutorialGroup] = useState({ label: '', value: '', id: '' });
  const [tutorialGroupErrorMsg, setTutorialGroupErrorMsg] = useState('');
  const [tutorialTitle, setTutorialTitle] = useState({ value: '', error: false, errorMsg: '' });
  const [tutorialLink, setTutorialLink] = useState({ value: '', error: false, errorMsg: '' });
  const [tutorialDescription, setTutorialDescription] = useState({ value: '', error: false, errorMsg: '' });
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewStatus, setPreviewStatus] = useState('empty');

  const { t } = useTranslation(namespace);

  const inputRef = useRef(null);

  const handleCreate = (newOpt: string) => {
    setTutorialGroup({ label: newOpt, value: newOpt, id: newOpt });
    groups.push({ label: newOpt, value: newOpt, id: newOpt });
  };

  const TutorialGroupHandle = (val: Group) => {
    if (typeof (val) === 'object') {
      setTutorialGroup(val);
      setTutorialGroupErrorMsg('');
    }
    if (val.value.length === 0) {
      setTutorialGroupErrorMsg(t('selectGroupRequired'));
    }
  };

  const tutorialTitleHandle = (val: string) => {
    const msg = verifyInput(val, true, 2, 40);
    setTutorialTitle({
      value: val,
      error: msg.length !== 0,
      errorMsg: msg,
    });
  };

  const getYoutubeEmbedUrl = (url: string) => {
    try {
      const parsedUrl = new URL(url);

      let videoId = null;

      if (parsedUrl.hostname === 'www.youtube.com' || parsedUrl.hostname === 'youtube.com') {
        videoId = parsedUrl.searchParams.get('v');
      }

      if (parsedUrl.hostname === 'youtu.be') {
        videoId = parsedUrl.pathname.substring(1);
      }

      if (!videoId) {
        return null;
      }

      return `https://www.youtube.com/embed/${videoId}`;
    } catch {
      return null;
    }
  };

  const updatePreview = (url: string) => {
    if (!url) {
      setPreviewUrl('');
      setPreviewStatus('empty');
      return;
    }

    const embedUrl = getYoutubeEmbedUrl(url);

    if (!embedUrl) {
      setPreviewUrl('');
      setPreviewStatus('error');
      return;
    }
    setPreviewUrl(embedUrl);
    setPreviewStatus('loading');
  };

  const tutorialLinkHandle = (val: string) => {
    const msg = verifyInput(val, true, 8);
    setTutorialLink({
      value: val,
      error: msg.length !== 0,
      errorMsg: msg,
    });

    updatePreview(val);
  };

  const tutorialDescriptionHandle = (val: string) => {
    const msg = verifyInput(val, true, 8);
    setTutorialDescription({
      value: val,
      error: msg.length !== 0,
      errorMsg: msg,
    });
  };

  const handleSubmit = async () => {
    const isValid = tutorialTitle.value
        && !tutorialTitle.error
        && tutorialGroup.value
        && tutorialLink.value
        && !tutorialLink.error
        && tutorialDescription.value
        && !tutorialDescription.error
        && previewStatus !== 'error'
        && previewStatus !== 'empty';
    if (isValid) {
      const tutorialInfos = {
        title: tutorialTitle.value,
        group: tutorialGroup.value,
        url: tutorialLink.value,
        description: tutorialDescription.value,
      };

      if (isEdit) {
        editTutorial(tutorialInfos);
      } else {
        createNewTutorial(tutorialInfos);
      }
    } else {
      tutorialTitleHandle(tutorialTitle.value);
      TutorialGroupHandle(tutorialGroup);
      tutorialDescriptionHandle(tutorialDescription?.value || '');
      tutorialLinkHandle(tutorialLink.value);
    }
  };

  useEffect(() => {
    if (isEdit) {
      const {
        title,
        group,
        url,
        description,
      } = tutorialToEdit;

      setTutorialTitle({ value: title, error: false, errorMsg: '' });
      setTutorialGroup({ label: group || '', value: group || '', id: group });
      setTutorialDescription({ value: description, error: false, errorMsg: '' });
      setTutorialLink({ value: url, error: false, errorMsg: '' });
      updatePreview(url);
    }
  }, [tutorialToEdit, isEdit]);

  return (
    <ExternalContainer>
      <ModalContainer>
        <NewTutorialContainer>
          <TutorialInfo>
            <TutorialFields>
              <FormLine>
                <InputWrapper>
                  {/* @ts-expect-error not typed at Select} */}
                  <InputTextLine
                    ref={inputRef}
                    errorMessage={tutorialTitle.errorMsg}
                    error={tutorialTitle.error}
                    name={tutorialTitle.value}
                    label={t('tutorialName')}
                    type="text"
                    value={tutorialTitle.value}
                    onChange={(e) => tutorialTitleHandle(e.target.value)}
                  />
                </InputWrapper>
              </FormLine>

              <FormLine>
                <SelectContainer>
                  <InputWrapper>
                    <Select
                      selectLabel={t('selectGroup')}
                      placeholder=""
                      isSearchable
                      isCreatable
                      fullWidth
                      options={groups}
                      placement="start"
                      // @ts-expect-error not typed at Select
                      value={tutorialGroup}
                      // @ts-expect-error not typed at Select
                      onChange={TutorialGroupHandle}
                      // @ts-expect-error not typed at Select
                      onCreateOption={handleCreate}
                      alphabeticalOrder
                      atModal
                    />
                  </InputWrapper>
                  {tutorialGroupErrorMsg.length > 0 && (
                    <span className="select-error-msg">
                      {tutorialGroupErrorMsg}
                    </span>
                  )}
                </SelectContainer>
              </FormLine>
            </TutorialFields>

            <Description>
              <TextArea
                // @ts-expect-error not typed at TextArea
                label={t('description')}
                error={tutorialDescription.error}
                errorMessage={tutorialDescription.errorMsg}
                value={tutorialDescription.value}
                onChange={(
                  e: React.ChangeEvent<HTMLTextAreaElement>,
                ) => tutorialDescriptionHandle(e.target.value)}
                styleContainer={{ width: '100%', height: '150px' }}
              />
            </Description>
          </TutorialInfo>

          <LinkSection>
            <InputWrapper>
              <InputTextLine
                errorMessage={tutorialLink.errorMsg}
                error={tutorialLink.error}
                name="TutorialLink"
                label={t('youtubeLink')}
                type="text"
                value={tutorialLink.value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => tutorialLinkHandle(e.target.value)}
                setValue={() => {}}
              />
            </InputWrapper>
          </LinkSection>
        </NewTutorialContainer>
        <Preview>
          {previewStatus === 'empty' && (
            <PreviewMessage error={false}>
              {t('youtubePreviewHint')}
            </PreviewMessage>
          )}

          {previewStatus === 'error' && (
            <PreviewMessage error>
              {t('invalidYoutubeLink')}
            </PreviewMessage>
          )}

          {previewUrl && (
            <PreviewVideo
              src={previewUrl}
              title={t('tutorialPreview')}
              referrerPolicy="strict-origin-when-cross-origin"
              onLoad={() => setPreviewStatus('success')}
            />
          )}
        </Preview>
      </ModalContainer>

      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={close}
          variant="outlined"
          size="large"
        >
          {t('cancel')}
        </Button>
        <Button
          style={{ margin: '5px' }}
          onClick={handleSubmit}
          size="large"
          disabled={
            tutorialGroup.value.length === 0
            && tutorialTitle.value.length === 0
            && tutorialDescription.value.length === 0
            && tutorialLink.value.length === 0
          }
        >
          {!isEdit ? t('create') : t('save')}
        </Button>
      </ActionsGroup>
    </ExternalContainer>
  );
};

export default AddTutorial;
